import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { TeamCommunicatorSelector } from '@/components/settings/TeamMode/TeamCommunicatorSelector'
import { Team } from '@/redux/types/team'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { RoomMember } from '@/redux/types/room'
import { Badge } from '@/components/ui/badge'

export interface RoundInfo {
  id: number
  round_number: number
  status: ActivityState
}

interface RoundTabsProps {
  loading: boolean
  teamsLoading: boolean
  roomLoading: boolean
  selectedRound: number
  setSelectedRound: (round: number) => void
  rounds: RoundInfo[]
  teams: Team[]
  teamMembers: Map<number, RoomMember[]>
  getCommunicatorForTeamRound: (
    teamId: number,
    roundNumber: number
  ) => RoomMember | null
  handleCommunicatorChange: (
    teamId: number,
    roundNumber: number,
    communicatorId: number | null
  ) => void
  isRoundStarted: (roundNumber: number) => boolean
  getRoundStatus: (roundNumber: number) => string
  isGameStarted: boolean
  handleApplyRound1ToAll: () => void
  handleClose: () => void
  handleSave: () => void
  saving: boolean
  hasPendingChanges: () => boolean
}

export function RoundTabs({
  loading,
  teamsLoading,
  roomLoading,
  selectedRound,
  setSelectedRound,
  rounds,
  teams,
  teamMembers,
  getCommunicatorForTeamRound,
  handleCommunicatorChange,
  isRoundStarted,
  getRoundStatus,
  isGameStarted,
  handleApplyRound1ToAll,
  handleClose,
  handleSave,
  saving,
  hasPendingChanges,
}: RoundTabsProps) {
  if (loading || teamsLoading || roomLoading) {
    return (
      <div className='flex-1 flex items-center justify-center'>
        <Loader2 className='w-8 h-8 animate-spin text-gray-400' />
      </div>
    )
  }

  return (
    <>
      <Tabs
        value={selectedRound?.toString() || '1'}
        onValueChange={(value) => setSelectedRound(parseInt(value, 10))}
        className='flex flex-col flex-1 min-h-0'
      >
        <div className='flex-shrink-0 px-6 py-4 border-b'>
          <TabsList
            className='grid w-full'
            style={{
              gridTemplateColumns: `repeat(${rounds.length}, 1fr)`,
            }}
          >
            {rounds.map((round) => (
              <TabsTrigger
                key={round.round_number ?? round.id}
                value={round.round_number?.toString() ?? round.id.toString()}
                className='relative'
              >
                Round {round.round_number ?? round.id}
                {round.status === ActivityState.InProgress && (
                  <span className='ml-2 text-green-600 text-xs font-semibold tracking-wide'>
                    Active
                  </span>
                )}
                {round.status === ActivityState.Completed && (
                  <span className='ml-2 text-gray-500 text-xs font-medium tracking-wide'>
                    Done
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className='overflow-auto flex-1 px-6 py-4 min-h-0'>
          {rounds.map((round) => {
            const roundNumber = round.round_number ?? round.id
            return (
              <TabsContent
                key={roundNumber}
                value={roundNumber.toString()}
                className='mt-4'
              >
                <div className='space-y-4'>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-lg font-semibold'>
                      Round {roundNumber} Communicators
                    </h3>
                    <div className='flex items-center gap-2'>
                      {roundNumber === 1 && rounds.length > 1 && (
                        <Button
                          size='sm'
                          onClick={handleApplyRound1ToAll}
                          disabled={saving}
                          className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                        >
                          Apply Round 1 to All
                        </Button>
                      )}
                      {isRoundStarted(roundNumber) && (
                        <Badge variant='outline' className='text-xs'>
                          Round has started - read only
                        </Badge>
                      )}
                    </div>
                  </div>

                  {teams.length === 0 ? (
                    <p className='text-muted-foreground text-center py-8'>
                      No teams available. Please create teams first.
                    </p>
                  ) : (
                    <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
                      {teams.map((team) => {
                        const members = teamMembers.get(team.id) || []
                        const currentCommunicator = getCommunicatorForTeamRound(
                          team.id,
                          roundNumber
                        )

                        return (
                          <div
                            key={team.id}
                            className='p-4 border rounded-lg space-y-2'
                          >
                            <h4 className='font-medium text-sm text-gray-700'>
                              {team.name}
                            </h4>
                            <TeamCommunicatorSelector
                              roundNumber={roundNumber}
                              roundStatus={getRoundStatus(roundNumber)}
                              teamMembers={members}
                              currentCommunicator={currentCommunicator}
                              onChange={(communicatorId) => {
                                handleCommunicatorChange(
                                  team.id,
                                  roundNumber,
                                  communicatorId
                                )
                              }}
                              isRequired={roundNumber === 1 && !isGameStarted}
                              isReadOnly={isRoundStarted(roundNumber)}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            )
          })}
        </div>
      </Tabs>

      <div className='flex-shrink-0 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t'>
        <div className='flex flex-col gap-3 justify-end sm:flex-row'>
          <Button
            variant='outline'
            onClick={handleClose}
            className='w-full transition-colors sm:w-auto hover:bg-gray-100'
            disabled={saving}
          >
            {hasPendingChanges() ? 'Cancel' : 'Close'}
          </Button>
          {hasPendingChanges() && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className='w-full transition-colors bg-dark-blue hover:bg-dark-blue-hovered sm:w-auto'
            >
              {saving ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
